<template>
  <div id="map-viewer-sidebar">
    <button type="button" class="filters-toggle" @click="filtersOpen = !filtersOpen">
      {{ filtersOpen ? 'Hide Filters' : 'Filters' }}
    </button>
    <div v-show="filtersOpen" id="map-viewer-data-filter" class="d-sm-block d-md-flex d-lg-flex align-items-center pb-2">
      <!-- date filter -->
      <div class="mx-2">
        <span class="d-md-block text-muted small text-left font-weight-bold">Select Range</span>
        <date-time-picker v-model="dateRange" is-range :masks="masks" />
      </div>

      <div class="mx-2">
        <span class="d-md-block text-muted small text-left font-weight-bold">Asset</span>
        <select name="" class="form-control" v-model="selectedAsset">
          <option v-for="asset in assets" :key="asset.massId" :value="asset.massId">{{ asset.massName }}</option>
        </select>
      </div>

      <div class="mx-2">
        <span class="d-sm-none d-md-block text-muted small text-left font-weight-bold">&nbsp;</span>
        <button class="btn btn-mantraz btn-apply" @click="onApplyFilter">Apply</button>
      </div>
    </div>
    <div v-if="isFilterDirty" class="text-warning mt-2 mx-2">
      Filters have changed. Click Apply to update the data.
    </div>

    <div class="mx-2 mt-2">
      <landmark-search-input v-model="selectedLandmark" label="Find Landmark" :landmarks="landmarks" />
    </div>

    <template v-if="isLoading">
      <div class="text-center mt-5">
        <b-spinner label="Spinning"></b-spinner>
      </div>
    </template>

    <template v-else-if="!isLoading && currAsset && currAsset.summary">
      <div id="map-viewer-asset-summary">
        <div id="map-viewer-asset-info">
          <h5 id="map-viewer-asset-info-name">{{ currAsset.massName }}</h5>
          <img id="map-viewer-asset-info-img" :src="currAsset.massImage !== '' ? '/api/' + currAsset.massImage : ''" :alt="currAsset.massName + 'Image'">
          <div id="map-viewer-asset-info-data">
            <div class="map-viewer-asset-info-data-item">
              <h6 class="map-viewer-asset-info-data-item-title">Type</h6>
              <p class="map-viewer-asset-info-data-item-value">{{ currAsset?.massMaty?.matyName }}</p>
            </div>

            <div class="map-viewer-asset-info-data-item">
              <h6 class="map-viewer-asset-info-data-item-title">Device Type</h6>
              <p class="map-viewer-asset-info-data-item-value">{{ currAsset?.massSystemType }}</p>
            </div>

            <!-- <div class="map-viewer-asset-info-data-item">
              <h6 class="map-viewer-asset-info-data-item-title">Status</h6>
              <span class="onhire-badge">ON HIRE</span>
              <span class="offhire-badge">OFF HIRE</span>
            </div> -->
          </div>
        </div>

        <div id="map-viewer-asset-accumulations">
          <div class="map-viewer-asset-accumulation-card">
            <h4 class="map-viewer-asset-accumulation-card-data">{{ $fmt(cruiseNm, 2) }} NM</h4>
            <span class="map-viewer-asset-accumulation-card-title">Total Cruise</span>
          </div>

          <div class="map-viewer-asset-accumulation-card">
            <h4 class="map-viewer-asset-accumulation-card-data">{{ $fmt(avgSpeedKnot, 2) }} KNOT</h4>
            <span class="map-viewer-asset-accumulation-card-title">Average Speed</span>
          </div>

          <div class="map-viewer-asset-accumulation-card">
            <h4 class="map-viewer-asset-accumulation-card-data">{{ $fmt((currAsset?.summary?.flowmeter?.summary?.meEngineCons), 2) }} LITER</h4>
            <span class="map-viewer-asset-accumulation-card-title">Total Engine Cons</span>
          </div>
        </div>
      </div>

      <div id="map-viewer-speed-legend">
        <div class="legend-row">
          <span class="legend-label">Slow</span>
          <span
            v-for="(c, i) in speedColors"
            :key="i"
            class="legend-chip-wrap"
            @mouseenter="onLegendHover(i)"
            @mouseleave="onLegendHover(null)">
            <span class="legend-chip" :style="{ background: c }"></span>
            <!-- Melayang, jadi kotak warnanya gak kegeser pas label muncul. -->
            <span v-if="legendHover === i && legendLabel" class="legend-value">{{ legendLabel }}</span>
          </span>
          <span class="legend-label">Fast</span>
        </div>
      </div>

      <div id="map-viewer-vessel-summary">
        <div id="map-viewer-vessel-card">
          <div id="map-viewer-vessel-header">
            <h4>Vessel Summary</h4>
            <span>Last Updated {{ formatDisplay(lastGpsData?.timestamp * 1000) }}</span>
          </div>

          <hr />

          <div id="map-viewer-vessel-location">
            <div id="map-viewer-vessel-first-location">
              <h5 class="map-viewer-vessel-latlong">{{ convertLatLngToDMS(lat(firstGpsData), lng(firstGpsData)) }}</h5>
              <span class="map-viewer-vessel-date">{{ formatDisplay(firstGpsData?.timestamp * 1000) }}</span>
              <p v-if="firstLocationText" class="map-viewer-vessel-location">{{ firstLocationText }}</p>
            </div>

            <div id="map-viewer-vessel-duration">
              <span>{{ toDuration(durationSec) }} Hours</span>
              <hr style="border-top: 2px solid #000000;" />
              <span>Duration</span>
            </div>

            <div id="map-viewer-vessel-last-location">
              <h5 class="map-viewer-vessel-latlong">{{ convertLatLngToDMS(lat(lastGpsData), lng(lastGpsData)) }}</h5>
              <span class="map-viewer-vessel-date">{{ formatDisplay(lastGpsData?.timestamp * 1000) }}</span>
              <p v-if="lastLocationText" class="map-viewer-vessel-location">{{ lastLocationText }}</p>
            </div>
          </div>

          <hr />

          <div class="map-viewer-vessel-container">
            <div
              v-for="(type, i) in currCapability.rpmTypes"
              :key="type"
              class="map-viewer-vessel-column"
              :class="i === 0 ? 'map-viewer-left-column' : 'map-viewer-right-column'">
              <h4>{{ rpmLabel(type) }} Engine</h4>
              <div>
                <h6 class="map-viewer-vessel-data-title">Average RPM</h6>
                <h4 class="map-viewer-vessel-data-value">{{ $fmt(currAsset?.summary?.rpm?.summary?.average?.[type], 2) }}</h4>
              </div>
              <div>
                <h6 class="map-viewer-vessel-data-title">Engine Running</h6>
                <h4 class="map-viewer-vessel-data-value">{{ toDuration(currAsset?.summary?.rpm?.summary?.runningTime?.[type]) }}</h4>
              </div>
              <template v-if="netFuel(type, 'engineCons') !== null">
                <div>
                  <h6 class="map-viewer-vessel-data-title">Fuel Cons{{ hasOutflow(type) ? ' (net)' : '' }}</h6>
                  <h4 class="map-viewer-vessel-data-value">{{ netFuel(type, 'engineCons') }} Liter</h4>
                </div>
                <div>
                  <h6 class="map-viewer-vessel-data-title">Fuel Flowrate{{ hasOutflow(type) ? ' (net)' : '' }}</h6>
                  <h4 class="map-viewer-vessel-data-value">{{ netFuel(type, 'avgFuelFlow') }} L/H</h4>
                </div>
              </template>
            </div>
          </div>
          <template v-if="orphanFuelGroups.length > 0">
            <hr style="border-top: 2px solid #E8ECF3;" />

            <div class="map-viewer-vessel-container">
              <div
                v-for="(name, i) in orphanFuelGroups"
                :key="name"
                class="map-viewer-vessel-column"
                :class="i % 2 === 0 ? 'map-viewer-left-column' : 'map-viewer-right-column'">
                <h4>{{ name }} Fuel</h4>
                <div>
                  <h6 class="map-viewer-vessel-data-title">Fuel Cons{{ hasOutflow(name) ? ' (net)' : '' }}</h6>
                  <h4 class="map-viewer-vessel-data-value">{{ netFuel(name, 'engineCons') }} Liter</h4>
                </div>
                <div>
                  <h6 class="map-viewer-vessel-data-title">Fuel Flowrate{{ hasOutflow(name) ? ' (net)' : '' }}</h6>
                  <h4 class="map-viewer-vessel-data-value">{{ netFuel(name, 'avgFuelFlow') }} L/H</h4>
                </div>
              </div>
            </div>
          </template>

          <template v-if="currCapability.aeCount > 0">
            <hr style="border-top: 2px solid #E8ECF3;" />

            <div id="map-viewer-vessel-ae">
              <div v-for="i in currCapability.aeCount" :key="i">
                <h6 class="map-viewer-vessel-data-title">Auxiliary Engine {{ i }}</h6>
                <h4 class="map-viewer-vessel-data-value">{{ toDuration(currAsset?.summary?.ae?.summary?.runningSeconds?.['AE' + i]) }} Hour</h4>
                <h4 class="map-viewer-vessel-data-value">{{ $fmt(currAsset?.summary?.ae?.summary?.fuelConsumption?.['AE' + i], 2) }} Liter</h4>
              </div>
            </div>
          </template>

          <hr />
          <div id="view-datalog-button" @click="() => $emit('toggleMenu')">
            <h6>View Datalog</h6>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import { toDuration, formatDateTime } from '@/util/date';
import { DISPLAY_DATETIME } from '@/util/apiDatetime';
import { locationDescription, describeLocation } from '@/util/ship/vesselDetail';
import { buildFmsSummary, getRpmTypeLabel } from '@/util/ship/fms-summary';
import { formatNum, parseNum } from '@/util/number';
import { capabilityFromSummary } from '@/util/summary/seriesToSummaryData';
import DateTimePicker from '@/components/directives/DateTimePicker.vue';
import LandmarkSearchInput from '@/components/map/LandmarkSearchInput.vue';
import { SPEED_COLORS } from '@/util/trail/trailColor';
import { metersToNm, kmhToKnot } from '@/util/units';

export default {
  name: 'MapViewerSidebar',
  components: {
    DateTimePicker,
    LandmarkSearchInput,
  },
  props: {
    isLoading: { type: Boolean, default: false },
    appliedMassId: {
      type: [Number, String],
      default: null,
    },
    isFilterDirty: {
      type: Boolean,
      default: false,
    },
    legendLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      masks: 'DD-MM-YYYY HH:mm',
      firstLocationText: null,
      locationReqId: 0,
      filtersOpen: window.innerWidth >= 576,
      selectedLandmark: null,
      legendHover: null,
      speedColors: SPEED_COLORS,
    };
  },
  computed: {
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());

        return { start: startDate, end: endDate };
      },
      set(val) {
        const start = moment(val.start);
        const end = moment(val.end);

        this.$store.dispatch('filter/setCustomDate', { start, end });
      },
    },
    selectedAsset: {
      get() {
        return this.currentMassId;
      },
      set(val) {
        this.$store.dispatch('filter/setCurrentMassId', val);
      },
    },
    ...mapState('asset', ['assets']),
    ...mapState('filter', {
      currentMassId: (state) => state.currentMassId,
      interval: (state) => state.interval,
      rangeType: (state) => state.rangeType,
    }),
    currAsset() {
      const currMassId = this.appliedMassId || this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) return null;

      return assets.find((asset) => asset.massId === currMassId);
    },
    currCapability() {
      return capabilityFromSummary(this.currAsset?.deviceStatus, this.currAsset?.summary);
    },
    // Group flowmeter tanpa posisi RPM senama; kalau gak dirender sendiri, datanya ilang.
    orphanFuelGroups() {
      const hasRpm = this.currCapability.rpmTypes;
      return Object.keys(this.fmGroups)
        .filter((n) => n !== 'meEngineCons' && n !== 'percentage' && !hasRpm.includes(n))
        .filter((n) => this.fmGroups[n]?.in);
    },
    fmGroups() {
      const built = buildFmsSummary({
        deviceStatus: this.currCapability,
        summary: this.currAsset?.summary,
        summarySource: this.currAsset?.summarySource,
        dateRange: this.dateRange,
        interval: 'hour',
      });
      return built?.fm || {};
    },
    firstGpsData() {
      if (!this.currAsset?.summary?.gps?.data) return null;
      const data = Object.values(this.currAsset?.summary?.gps?.data);
      return data ? data[0] : null;
    },

    // Posisi akhir di rentang yang dilaporkan, bukan posisi live.
    lastGpsData() {
      const last = this.currAsset?.summary?.gps?.summary?.lastPosition;
      if (last?.latitude !== undefined) return last;
      return this.currAsset?.lastGpsData || null;
    },
    lastLocationText() {
      return locationDescription({ locationInfo: this.currAsset?.summary?.gps?.summary?.zone });
    },
    // Komponen carinya nampilin displayName; store cuma ngasih lndmName.
    landmarks() {
      return (this.$store.getters['landmark/landmarks'] || [])
        .map((l) => ({ ...l, displayName: l.displayName || l.lndmName }));
    },
    // totalPolyLength = panjang lintasan. `distance` itu jarak lurus awal-akhir.
    cruiseNm() {
      return metersToNm(this.currAsset?.summary?.gps?.summary?.totalPolyLength || 0);
    },
    avgSpeedKnot() {
      return kmhToKnot(this.currAsset?.summary?.gps?.summary?.averageSpeed || 0);
    },
    durationSec() {
      return (this.lastGpsData?.timestamp || 0) - (this.firstGpsData?.timestamp || 0);
    },
  },
  watch: {
    selectedLandmark(id) {
      if (id === null) return;
      const l = this.landmarks.find((x) => String(x.lndmId) === String(id));
      if (l) this.$emit('fly-to', { lat: Number(l.lndmLatitude), lng: Number(l.lndmLongitude) });
    },
    firstGpsData: {
      immediate: true,
      handler(data, oldData) {
        if (!data?.latitude || !data?.longitude) {
          this.firstLocationText = null;
          return;
        }
        if (data.latitude === oldData?.latitude && data.longitude === oldData?.longitude) {
          return;
        }
        // Penanda sebelum request; geocode yang telat gak boleh nimpa label baru.
        const reqId = ++this.locationReqId;
        describeLocation(data.latitude, data.longitude).then((text) => {
          if (reqId !== this.locationReqId) return;
          this.firstLocationText = text;
        });
      },
    },
  },
  mounted() { },
  methods: {
    onLegendHover(i) {
      this.legendHover = i;
      this.$emit('legend-hover', i);
    },
    // Flowmeter in-out: konsumsi sebenarnya = masuk dikurangi balik.
    hasOutflow(name) {
      return !!this.fmGroups[name]?.out;
    },
    netFuel(name, field) {
      const g = this.fmGroups[name];
      if (!g?.in) return null;
      const inflow = parseNum(g.in[field] || 0) || 0;
      const outflow = parseNum(g.out?.[field] || 0) || 0;
      return formatNum(inflow - outflow, 2);
    },
    lat(t) {
      return t?.latLong ? t.latLong[0] : t?.latitude;
    },
    lng(t) {
      return t?.latLong ? t.latLong[1] : t?.longitude;
    },
    rpmLabel(type) {
      const label = getRpmTypeLabel(type, this.currCapability.rpmTypes);
      return String(label).charAt(0).toUpperCase() + String(label).slice(1).toLowerCase();
    },
    onApplyFilter() {
      this.$emit('applyFilter', {
        dateRange: this.dateRange,
        massId: this.selectedAsset,
      });
    },
    convertLatLngToDMS(latitude, longitude) {
      if (latitude === null || longitude === null) return '-, -';

      const latDegrees = Math.floor(Math.abs(latitude));
      const latMinutesDecimal = (Math.abs(latitude) - latDegrees) * 60;
      const latMinutes = Math.floor(latMinutesDecimal);
      const latSeconds = Math.round((latMinutesDecimal - latMinutes) * 60);

      const lngDegrees = Math.floor(Math.abs(longitude));
      const lngMinutesDecimal = (Math.abs(longitude) - lngDegrees) * 60;
      const lngMinutes = Math.floor(lngMinutesDecimal);
      const lngSeconds = Math.round((lngMinutesDecimal - lngMinutes) * 60);

      const latDirection = latitude >= 0 ? 'N' : 'S';
      const lngDirection = longitude >= 0 ? 'E' : 'W';

      return `${latDegrees}° ${latMinutes}' ${latSeconds}'' ${latDirection}, ${lngDegrees}° ${lngMinutes}' ${lngSeconds}'' ${lngDirection}`;
    },
    toDuration,
    formatDateTime,
    formatDisplay: (v) => formatDateTime(v, DISPLAY_DATETIME),
    moment,
  },
};
</script>

<style scoped>
#map-viewer-sidebar {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 420px;
  z-index: 1100;
  background: #FFFFFF;
  overflow-y: scroll;
  transition: transform 0.2s ease;
}

#map-viewer-sidebar.panel-closed {
  transform: translateX(-100%);
}

#map-viewer-sidebar::-webkit-scrollbar {
  display: none;
}

.filters-toggle {
  border: 0;
  border-radius: 6px;
  background: var(--mz-primary-gradient);
  color: #ffffff;
  font-weight: 700;
  font-size: 12px;
  padding: 4px 14px;
  margin: 8px 0 0 8px;
}

/* Sisa bawah dipakai label melayang, jadi gak nabrak kartu di bawahnya. */
#map-viewer-speed-legend {
  padding: 0 15px 24px 15px;
}

.legend-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-chip-wrap {
  position: relative;
  display: inline-flex;
}

.legend-chip {
  width: 18px;
  height: 10px;
  border-radius: 2px;
}

.legend-label {
  font-size: 11px;
  color: #6b7280;
}

.legend-chip {
  cursor: default;
}

.legend-value {
  position: absolute;
  top: calc(100% + 3px);
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  font-weight: 700;
  color: #374151;
  white-space: nowrap;
}

@media (min-width: 576px) {
  .filters-toggle {
    display: none;
  }
}

@media (max-width: 767px) {
  #map-viewer-sidebar {
    width: 100%;
  }
}

#map-viewer-asset-summary {
  display: grid;
  grid-template-columns: 3fr 2fr;
  grid-gap: 10px;
  padding: 10px 15px 10px 15px;
}

#map-viewer-asset-info {
  display: flex;
  flex-direction: column;
  border: 2px solid #E8ECF3;
  padding: inherit;
  border-radius: 20px;
}

#map-viewer-asset-info-img {
  width: inherit;
  height: 120px;
  object-fit: cover;
}

#map-viewer-asset-info-data {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: 5px;
  margin-top: 10px;
}

.map-viewer-asset-info-data-item-title {
  font-size: 12px;
  line-height: 22px;
  color: #768499;
}

.map-viewer-asset-info-data-item-value {
  font-size: 14px;
  line-height: 22px;
  color: #1E1E1E;
}

.onhire-badge {
  background-color: #55C95A;
  color: #FFFFFF;
  padding: 5px 10px;
  border-radius: 10px;
  font-size: 12px;
}

.offhire-badge {
  background-color: #FF0000;
  color: #FFFFFF;
  padding: 5px 10px;
  border-radius: 10px;
  font-size: 12px;
}

#map-viewer-asset-accumulations {
  display: grid;
  grid-template-rows: repeat(3, minmax(0, 1fr));
  grid-gap: 5px;
}

.map-viewer-asset-accumulation-card {
  display: flex;
  flex-direction: column;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
  padding: 10px 15px;
}

.map-viewer-asset-accumulation-card-data {
  color: #264284;
}

.map-viewer-asset-accumulation-card-title {
  color: #425166;
}

#map-viewer-vessel-summary {
  padding: 10px 15px 10px 15px;
}

#map-viewer-vessel-summary hr {
  width: 100%;
  margin: 0;
  padding: 0;
}

#map-viewer-vessel-card {
  border: 2px solid #E8ECF3;
  border-radius: 20px
}

#map-viewer-vessel-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
}

#map-viewer-vessel-location {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
}

#map-viewer-vessel-first-location {
  text-align: left;
}

#map-viewer-vessel-duration {
  text-align: center;
}

#map-viewer-vessel-last-location {
  text-align: right;
}

.map-viewer-vessel-latlong {
  color: var(--mz-primary-ink);
  font-size: 16px;
  line-height: 24px;
}

.map-viewer-vessel-date {
  color: #768499;
  font-size: 12px;
  line-height: 22px;
}

.map-viewer-vessel-location {
  color: #768499;
  font-size: 12px;
  line-height: 22px;
  margin: 0;
}

/* Jumlah kolom ngikut sensor kapal, jadi wajib bisa turun baris. */
.map-viewer-vessel-container {
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;
}

.map-viewer-vessel-column {
  flex: 1 1 170px;
  min-width: 0;
  padding: 15px 20px;
}

.map-viewer-left-column {
  border-right: 2px solid #E8ECF3;
}

#map-viewer-vessel-ae {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: flex-start;
  padding: 15px 20px;
}

#map-viewer-vessel-ae > div {
  flex: 1 1 110px;
  min-width: 0;
}

.map-viewer-vessel-data-title {
  color: #3F4C5F;
  font-size: 14px;
  line-height: 22px;
}

.map-viewer-vessel-data-value {
  color: #264284;
  font-size: 20px;
  line-height: 30px;
}

#view-datalog-button {
  padding: 15px 20px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background: var(--mz-primary-gradient);
  border-radius: 0px 0px 20px 20px;
}

#view-datalog-button h6 {
  color: #FFFFFF;
  font-size: 16px;
  line-height: 24px;
}

#view-datalog-button:hover {
  cursor: pointer;
}
</style>
